import { Chip, Grid, Icon, Paper } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { EntitlementGroupInterface } from '@thro-types/access-handler';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useEntitlementGroupMutation, useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import { useFacultiesQuery } from 'api/hooks/useFacultiesQuery';
import { CreateButton, TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import EntitlementGroupDetail from './EntitlementGroupDetail/EntitlementGroupDetail';
import { EntitlementGroupModal } from './forms/entitlement-group-form/entitlement-group.form';

export function EntitlementGroups() {
  const [currentlyEditingEntitlementGroup, setCurrentlyEditingEntitlementGroup] = useState<
    EntitlementGroupInterface | undefined
  >(undefined);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  usePageHeading({
    heading: t(translations.pages.entitlementGroups.title()),
    headingProps: {
      back: location.state?.back ?? `/settings/access-handler`
    }
  });

  const { data: faculties } = useFacultiesQuery();

  const {
    data: entitlementGroupsData,
    isError,
    isLoading
  } = useEntitlementGroupsQuery(true, {
    params: {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }
  });

  const { deletion } = useEntitlementGroupMutation();

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleDelete = async () => {
    if (!currentlyEditingEntitlementGroup || deletion.isPending) return;
    try {
      await deletion.mutateAsync(currentlyEditingEntitlementGroup.id);
      dispatch(
        AlertActions.success(t(translations.pages.entitlementGroups.alerts.deleteSuccess()))
      );
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.entitlementGroups.alerts.deleteFailed())));
    } finally {
      setDeleteModalOpen(false);
      setCurrentlyEditingEntitlementGroup(undefined);
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams<EntitlementGroupInterface>) => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setCurrentlyEditingEntitlementGroup(params.row);
            setCreateModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => {
            setCurrentlyEditingEntitlementGroup(params.row);
            setDeleteModalOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<EntitlementGroupInterface> => {
    return [
      {
        field: 'id',
        flex: 1,
        headerName: t(translations.common.id()),
        align: 'center'
      },
      {
        field: 'name',
        flex: 6,
        headerName: t(translations.pages.entitlementGroups.tableHeader.entitlementGroup()),
        renderCell: params => (
          <EntitlementGroupDetail entitlementGroup={params.row} faculties={faculties} />
        )
      },
      {
        field: 'entitlements',
        flex: 4,
        headerName: t(translations.pages.entitlementGroups.tableHeader.entitlements()),
        renderCell: params => (
          <Grid container style={{ height: 'fit-content' }}>
            {params.row.entitlements?.map(ent => (
              <Chip key={ent.id} label={ent.name} style={{ margin: '0.25em' }} />
            ))}
          </Grid>
        )
      },
      {
        field: 'roles',
        flex: 4,
        headerName: t(translations.pages.entitlementGroups.tableHeader.roles()),
        renderCell: params =>
          params.row.roles?.map(role => <Chip key={role.id} label={role.name} />)
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [faculties, rowActions, t]);

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  return (
    <Grid container>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.entitlementGroups.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          handleDelete();
        }}
        isPending={deletion.isPending}
      >
        <div>{t(translations.pages.entitlementGroups.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="entitlementGroupModal">
        <Modal
          open={createModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCreateModalOpen(false);
          }}
          headline={'Entitlement-Group'}
          onCancel={() => setCreateModalOpen(false)}
          disabledCancel={disabledCancel}
          width="half"
        >
          <EntitlementGroupModal
            closeHandler={() => {
              setCreateModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
            faculties={faculties}
            entitlementGroup={currentlyEditingEntitlementGroup}
          />
        </Modal>
      </ErrorBoundary>
      <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} marginBottom={'1em'}>
        <CreateButton
          onClick={() => {
            setCurrentlyEditingEntitlementGroup(undefined);
            setCreateModalOpen(true);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <Grid container style={{ height: '70vh' }}>
            <DataGrid
              getEstimatedRowHeight={() => 100}
              getRowHeight={() => 'auto'}
              style={{ height: '100%', width: '100%' }}
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              columns={columns}
              loading={isLoading}
              rows={entitlementGroupsData?.data || []}
              rowCount={entitlementGroupsData?.pagination.count || 0}
              paginationMode="server"
              paginationModel={{
                page: paginationModel.page,
                pageSize: paginationModel.pageSize
              }}
              onPaginationModelChange={pagModel => {
                handlePaginationChange(pagModel);
              }}
            />
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
